import React, { Component } from "react";
import { ApiError, getTokenUsageOverview, type TokenUsageOverviewResponse } from "../../api";
import { getCompanionSession } from "../../auth/session";
import "./UsagePage.scss";

interface UsagePageProps {}

interface UsagePageState {
  loading: boolean;
  errorMessage: string | null;
  response: TokenUsageOverviewResponse | null;
}

class UsagePage extends Component<UsagePageProps, UsagePageState> {
  constructor(props: UsagePageProps) {
    super(props);
    this.state = {
      loading: true,
      errorMessage: null,
      response: null,
    };
  }

  componentDidMount(): void {
    void this.loadUsageOverview();
  }

  async loadUsageOverview(): Promise<void> {
    this.setState({ loading: true, errorMessage: null });
    try {
      const session = getCompanionSession();
      if (!session) {
        this.setState({
          loading: false,
          errorMessage: "Missing local session. Please login again.",
          response: null,
        });
        return;
      }
      const response = await getTokenUsageOverview({
        xSid: session.sid,
        xEmail: session.email,
      });
      this.setState({ response, loading: false });
    } catch (error: unknown) {
      const message =
        error instanceof ApiError
          ? `${error.message} (${error.status})`
          : error instanceof Error
            ? error.message
            : "Unknown error";
      this.setState({ errorMessage: message, response: null, loading: false });
    }
  }

  private formatCurrencyFromCents(valueInCents: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(valueInCents / 100);
  }

  private getSafeAmount(value?: number): number {
    if (typeof value !== "number" || Number.isNaN(value) || value < 0) {
      return 0;
    }
    return value;
  }

  render(): React.JSX.Element {
    const { loading, errorMessage, response } = this.state;
    const maxLimit = this.getSafeAmount(response?.max_limit);
    const usage = this.getSafeAmount(response?.usage);
    const spend = response?.spend ?? [];
    const percentUsed = maxLimit > 0 ? Math.min((usage / maxLimit) * 100, 100) : 0;
    const peakDailySpend = spend.reduce((max, item) => Math.max(max, this.getSafeAmount(item.cents)), 0);
    const hasSpendData = spend.length > 0;
    const hasNonZeroSpend = peakDailySpend > 0;
    const now = new Date();
    const currentMonthName = now.toLocaleString("en-US", { month: "long" });
    const nextMonthName = new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleString("en-US", { month: "long" });

    return (
      <div className="usage-page">
        <section className="usage-page-cards" aria-label="Usage overview cards">
          <article className="usage-page-card">
            <div className="usage-page-card-header">
              <h2 className="usage-page-card-title">Monthly Usage Overview</h2>
              <div className="usage-page-card-subs">You have used {percentUsed.toFixed(1)}% of your monthly limit in the current billing period.</div>
            </div>
            {loading ? <p className="usage-page-card-status">Loading usage data...</p> : null}
            {!loading && errorMessage ? (
              <p className="usage-page-card-error">{errorMessage}</p>
            ) : null}
            {!loading && !errorMessage ? (
              <>
                <div className="usage-page-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(percentUsed)}>
                  <div className="usage-page-progress-fill" style={{ width: `${percentUsed}%` }} />
                </div>
                <div className="usage-page-progress-values">
                  <div>
                    <p className="usage-page-kpi-label">Current usage</p>
                    <p className="usage-page-kpi-value">{this.formatCurrencyFromCents(usage)}</p>
                  </div>
                  <div>
                    <p className="usage-page-kpi-label">Monthly limit</p>
                    <p className="usage-page-kpi-value" style={{ textAlign: "right" }}>{this.formatCurrencyFromCents(maxLimit)}</p>
                  </div>
                </div>
              </>
            ) : null}
          </article>

          <article className="usage-page-card">
            <div className="usage-page-card-header">
              <h2 className="usage-page-card-title">Daily Usage Trend</h2>
              <div className="usage-page-card-subs">Your usage per day across this billing period i.e. 1st of {currentMonthName} to 1st of {nextMonthName}.</div>
            </div>
            {loading ? <p className="usage-page-card-status">Loading spend data...</p> : null}
            {!loading && errorMessage ? (
              <p className="usage-page-card-error">{errorMessage}</p>
            ) : null}
            {!loading && !errorMessage ? (
              <>
                {hasSpendData ? (
                  <div className="usage-page-chart" role="img" aria-label="Daily spend chart">
                    {spend.map((item, index) => {
                      const cents = this.getSafeAmount(item.cents);
                      const heightPercent = hasNonZeroSpend ? Math.max((cents / peakDailySpend) * 100, 1) : 1;
                      const tooltipText = `${cents.toFixed(2)} cents on ${item.date}`;
                      return (
                        <div className="usage-page-chart-col" key={`${item.date}-${index}`}>
                          <div className="usage-page-chart-bar-wrap">
                            <div className="usage-page-chart-bar" style={{ height: `${heightPercent}%` }} title={tooltipText} />
                          </div>
                          <span className="usage-page-chart-label" title={tooltipText}>{item.date.slice(8, 10)}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="usage-page-card-status">No spend data for this month yet.</p>
                )}
                {hasSpendData && !hasNonZeroSpend ? (
                  <p className="usage-page-chart-hint">No spend recorded yet for the selected period.</p>
                ) : null}
              </>
            ) : null}
          </article>
        </section>
      </div>
    );
  }
}

export default UsagePage;
